import React from 'react';
import classNames from 'classnames';
import styles from './AreenaFooter.module.scss';
import YleAreenaLogo from 'assets/areena.svg';
import YleLogo from 'assets/yle.svg';
import BarnensArenanLogo from 'assets/barnensArena.svg';
import { serviceRoutes } from 'utils/routes';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { useTranslation } from 'hooks/useTranslation';
import { Container } from 'components/Container';
import { getCanonicalBaseURL, getCanonicalURL } from 'utils/url';
import { Control } from 'components/Controls/Control';
import { DSLink } from 'components/DSLink';
import { Link } from 'components/Link/Link';
import { useRouter } from 'next/router';

export const FooterSwedish: React.FunctionComponent = () => {
  const { areenaService = 'tv' } = useAreenaService();
  const t = useTranslation();
  const { asPath: path, query } = useRouter();

  const finnishFrontPageURL = `${getCanonicalBaseURL('fi')}/${
    serviceRoutes[areenaService]['fi']
  }`;

  const finnishAlternativeUrl =
    getCanonicalURL(path, 'fi', query) || finnishFrontPageURL;

  return (
    <div className={styles.footer}>
      <Container>
        <div className={classNames(styles.areenaLinksSection)}>
          <Link
            className={styles.areenaLink}
            noNextLink
            pointer={{
              uri: finnishAlternativeUrl,
            }}
          >
            <YleAreenaLogo aria-hidden className={styles.areenaLogo} />
            <div className={styles.areenaLinkText}>
              {t('otherLanguageLink')}
            </div>
          </Link>

          <Link
            className={styles.areenaLink}
            noNextLink
            pointer={{
              uri: `${getCanonicalBaseURL('sv')}/${serviceRoutes['children']['sv']}`,
            }}
          >
            <BarnensArenanLogo aria-hidden className={styles.areenaLogo} />
            <div className={styles.areenaLinkText}>{t('lastenAreena')}</div>
          </Link>
        </div>
        <div className={styles.generalLinksSection}>
          <div className={styles.subLinksSection}>
            <h3 className={styles.subTitle}>{t('footerSupportTitle')}</h3>
            <div className={styles.linksContainer}>
              {' '}
              <DSLink
                pointer={{
                  uri: `https://svenska.yle.fi/s/10046447`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                aria-label={t('tvApps')}
                text={t('tvApps')}
                textSize="s"
              ></DSLink>
              <DSLink
                pointer={{
                  uri: `https://svenska.yle.fi/s/10046434`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                text={t('androidApps')}
                textSize="s"
                aria-label={t('androidApps')}
              ></DSLink>
              <DSLink
                pointer={{
                  uri: `https://svenska.yle.fi/s/10046444`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                aria-label={t('iPhoneAndiPad')}
                text={t('iPhoneAndiPad')}
                textSize="s"
              ></DSLink>
              <DSLink
                pointer={{
                  uri: `https://svenska.yle.fi/arenan/instruktioner`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                aria-label={t('allInstructions')}
                text={t('allInstructions')}
                textSize="s"
              ></DSLink>
            </div>
          </div>
          <div className={styles.subLinksSection}>
            <h3 className={styles.subTitle}>{t('footerSomeTitle')}</h3>
            <div className={styles.linksContainer}>
              <DSLink
                pointer={{
                  uri: `https://www.facebook.com/yleareena`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                aria-label={t('facebook')}
                text={t('facebook')}
                textSize="s"
              ></DSLink>
              <DSLink
                pointer={{
                  uri: `https://www.instagram.com/yleareena`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                aria-label={t('instagram')}
                text={t('instagram')}
                textSize="s"
              ></DSLink>
              <DSLink
                pointer={{
                  uri: `https://www.facebook.com/svenskayle`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                text={t('arenanFacebook')}
                textSize="s"
                aria-label={'tikTok'}
              ></DSLink>
              <DSLink
                pointer={{
                  uri: `https://www.instagram.com/svenskayle`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                aria-label={t('youtube')}
                text={t('arenanInstagram')}
                textSize="s"
              ></DSLink>
            </div>
          </div>
          <div className={classNames(styles.subLinksSection)}>
            <h3 className={styles.subTitle}>{t('footerFeedbackTitle')}</h3>
            <div className={styles.linksContainer}>
              <DSLink
                pointer={{
                  uri: `https://kundservice.yle.fi/csp`,
                  type: 'external-html',
                }}
                linkVariant="internal"
                aria-label={t('customerService')}
                text={t('customerService')}
                textSize="s"
              ></DSLink>
            </div>
          </div>
        </div>
      </Container>
      <div className={styles.yleFiLinkContainer}>
        <Container>
          <Link
            pointer={{ uri: `https://svenska.yle.fi`, type: 'external-html' }}
            className={styles.yleFiLink}
          >
            <div className={styles.yleFiLinkContent}>
              <YleLogo className={styles.yleLogo} />
              <span className={styles.yleLinkText}>{t('yleUrl')}</span>
            </div>
          </Link>
        </Container>
      </div>
      <div className={styles.yleRelatedLinkContainer}>
        <Container>
          <div className={styles.yleRelatedLinks}>
            <DSLink
              pointer={{
                uri: `https://svenska.yle.fi/bolaget`,
                type: 'external-html',
              }}
              aria-label={t('yleCorporation')}
              text={t('yleCorporation')}
              textSize="s"
              theme="light"
              linkVariant="internal"
            ></DSLink>
            <DSLink
              pointer={{
                uri: `https://svenska.yle.fi/kontaktuppgifter`,
                type: 'external-html',
              }}
              aria-label={t('yleContactInfo')}
              text={t('yleContactInfo')}
              textSize="s"
              theme="light"
              linkVariant="internal"
            ></DSLink>
            <DSLink
              pointer={{
                uri: `https://svenska.yle.fi/s/cookiepolicy`,
                type: 'external-html',
              }}
              aria-label={t('cookiePolicy')}
              text={t('cookiePolicy')}
              textSize="s"
              theme="light"
              linkVariant="internal"
            ></DSLink>
            <Control
              as={'link'}
              controlKey={'cookieSettings'}
              control={{
                type: 'activator',
                title: t('cookieSettings'),
                tag: 'cookie-consent',
                functionality: {
                  id: 'cookie-consent',
                },
              }}
            ></Control>
            <DSLink
              pointer={{
                uri: `https://svenska.yle.fi/s/dataskyddsbeskrivning`,
                type: 'external-html',
              }}
              aria-label={t('privacyPolicy')}
              textSize="s"
              theme="light"
              linkVariant="internal"
              text={t('privacyPolicy')}
            ></DSLink>
          </div>
        </Container>
      </div>
    </div>
  );
};
